import { lazy } from 'react';
import MainLayout from 'layout/MainLayout';
import Loadable from 'components/common/Loadable/Loadable';
import AuthGuard from 'lib/route-guard/AuthGuard';
import WorkflowGuard from 'lib/route-guard/WorkflowGuard';
import { REMOTE_FILE_MANAGEMENT_ALLOWED } from 'config';
import {
    dashboardWorkflowsPath,
    workflowExecutionLogsPath,
    userProfilePath,
    projectDetailsPath,
    projectListPath,
    mgmtLocationsPath,
    mgmtKillSwitchPath,
    mgmtKillSwitchDetailPath,
    projectResourcesPath,
    projectUsersPath,
    workflowDetailsPath,
    workflowExecutePath,
    workflowExecutionDetailsPath,
    workflowListPath,
    mgmtResourceAssignmentsPath,
    error500Path,
    underConstructionPath,
    mgmtResourcesPath,
    containerListPath,
    workflowCreateHpcAppListPath,
    containerCreatePath,
    workflowCreateContainerConfigPath,
    containerDetailsPath,
    workflowCreateHpcAppConfigPath,
    workflowCreateJobScriptConfigPath,
    dashboardDatasetsPath,
    projectManageTagsPath,
    mgmtProvidersPath,
    mgmtUsersPath,
    mailResourceRequestPath,
    projectAccessRequestsPath,
    dashboardGeneralPath,
    jobScriptsListPath,
    jobScriptCreatePath,
    workflowCreateJobScriptListPath,
    jobScriptExecutionLogsPath,
    datasetCreateEmptyPath,
    workflowExecutionReExecutePath,
    jobScriptDuplicatePath,
    dashboardTransferStatusStagePath,
    dashboardTransferStatusDeletePath,
    containerUpdatePath,
    mgmtProjectRequestsPath,
    projectResourceRequestsPath,
    projectOwnRequestsPath,
    dashboardDdiPath,
    userExpirioPath,
    projectCommandTemplatesMgmtPath,
    workflowCreatePath,
    remoteFileManagerPath,
    datasetListPath,
    datasetUploadPath,
    datasetDetailsPath,
    datasetUpdatePath,
    datasetFileListPath,
    datasetEditFilePath,
    workflowCreateContainerListPath,
    mgmtResourceRequestsPath,
    projectResourceUsagePath,
    dashboardResourceUsagePath,
    mgmtCommandTemplatesPath,
} from './paths';
import InstanceGuard from 'lib/route-guard/InstanceGuard';
import { InstanceType } from 'types/enums';

const MaintenanceError404Page = Loadable(lazy(() => import('pages/Maintenance/404')));
const MaintenanceError500Page = Loadable(lazy(() => import('pages/Maintenance/500')));
const MaintenanceUnderConstructionPage = Loadable(lazy(() => import('pages/Maintenance/UnderConstruction')));
const MaintenanceComingSoonPage = Loadable(lazy(() => import('pages/Maintenance/ComingSoon')));

const DashboardWorkflowsPage = Loadable(lazy(() => import('pages/Dashboard/Workflows')));
const DashboardResourceUsagePage = Loadable(lazy(() => import('pages/Dashboard/ResourceUsage')));
const DashboardGeneralPage = Loadable(lazy(() => import('pages/Dashboard/General')));
const DashboardDdiPage = Loadable(lazy(() => import('pages/Dashboard/DDI')));
const DashboardTransferStatusStagePage = Loadable(lazy(() => import('pages/Dashboard/TransferStatuses/Stage')));
const DashboardTransferStatusDeletePage = Loadable(lazy(() => import('pages/Dashboard/TransferStatuses/Delete')));

const WorkflowListPage = Loadable(lazy(() => import('pages/Workflows')));
const WorkflowCreatePage = Loadable(lazy(() => import('pages/Workflows/WorkflowsCreate')));
const WorkflowDetailsPage = Loadable(lazy(() => import('pages/Workflows/WorkflowDetails')));
const WorkflowExecutePage = Loadable(lazy(() => import('pages/Workflows/Execute')));
const WorkflowExecutionDetailsPage = Loadable(
    lazy(() => import('pages/Workflows/WorkflowExecutions/WorkflowExecutionDetails'))
);
const WorkflowExecutionReExecutePage = Loadable(lazy(() => import('pages/Workflows/WorkflowExecutions/ReExecute')));
const WorkflowExecutionLogsPage = Loadable(
    lazy(
        () =>
            import('pages/Workflows/WorkflowExecutions/WorkflowExecutionDetails/Tabs/WorkflowExecutionProgressTab/Log')
    )
);

const WorkflowCreateContainerListPage = Loadable(lazy(() => import('pages/Workflows/CreateWorkflowWithContainer')));
const WorkflowCreateContainerConfigPage = Loadable(
    lazy(() => import('pages/Workflows/CreateWorkflowWithContainer/ContainerWorkflowCreate'))
);
const WorkflowCreateHpcAppListPage = Loadable(lazy(() => import('pages/Workflows/CreateWorkflowWithHPCApplication')));
const WorkflowCreateHpcAppConfigPage = Loadable(
    lazy(() => import('pages/Workflows/CreateWorkflowWithHPCApplication/HPCApplicationWorkflowCreate'))
);
const WorkflowCreateJobScriptPage = Loadable(lazy(() => import('pages/Workflows/CreateWorkflowWithJobScript')));
const WorkflowCreateJobScriptConfigPage = Loadable(
    lazy(() => import('pages/Workflows/CreateWorkflowWithJobScript/JobscriptWorkflowCreate'))
);

const DatasetListPage = Loadable(lazy(() => import('pages/Datasets')));
const DatasetUploadPage = Loadable(lazy(() => import('pages/Datasets/UploadDataset')));
const DatasetDetailsPage = Loadable(lazy(() => import('pages/Datasets/DatasetDetails')));
const DatasetUpdatePage = Loadable(lazy(() => import('pages/Datasets/DatasetDetails/UpdateDatasetMetadata')));
const DatasetCreateEmptyPage = Loadable(lazy(() => import('pages/Datasets/CreateEmptyDataset')));
const DatasetFileListPage = Loadable(lazy(() => import('pages/Datasets/DatasetDetails/FileList')));
const DatasetEditFilePage = Loadable(lazy(() => import('pages/Datasets/DatasetDetails/FileList/EditFile')));

const ProjectListPage = Loadable(lazy(() => import('pages/Projects')));
const ProjectDetailsPage = Loadable(lazy(() => import('pages/Projects/ProjectsDetails')));
const ProjectAccessRequestsPage = Loadable(lazy(() => import('pages/Projects/ProjectsDetails/AccessRequests')));
const ProjectOwnRequestsPage = Loadable(lazy(() => import('pages/Projects/ProjectSentProjectRequests')));
const ProjectResourceRequestsPage = Loadable(
    lazy(() => import('pages/Projects/ProjectsDetails/ProjectSentResourceRequests'))
);
const ProjectManageTagsPage = Loadable(lazy(() => import('pages/Projects/TagsManage')));
const ProjectUsersPage = Loadable(lazy(() => import('pages/Users')));
const ProjectResourcesPage = Loadable(lazy(() => import('pages/Projects/Resources')));
const ProjectCommandTemplatesMgmtPage = Loadable(
    lazy(() => import('pages/Projects/ProjectsDetails/CommandTemplatesManagement'))
);
const ProjectResourceCyclopsPage = Loadable(lazy(() => import('pages/Projects/Resources/CyclopsUsagePage')));
// const ProjectConsumptionDashboardPage = Loadable(lazy(() => import('pages/Projects/ProjectConsumptionDashboard')));

const MgmtUsersPage = Loadable(lazy(() => import('pages/Management/Users')));
const MgmtProjectRequestsPage = Loadable(lazy(() => import('pages/Management/Requests/ProjectRequestsManagement')));
const MgmtResourceRequestsPage = Loadable(lazy(() => import('pages/Management/Requests/ResourceRequestsManagement')));
const MgmtLocationsPage = Loadable(lazy(() => import('pages/Management/Locations')));
const MgmtProvidersPage = Loadable(lazy(() => import('pages/Management/Providers')));
const MgmtResourceAssignmentsPage = Loadable(lazy(() => import('pages/Management/Resources/ResourceAssignments')));
const MgmtResourcesPage = Loadable(lazy(() => import('pages/Management/Resources')));
const MgmtResourceRequestsManagePage = Loadable(
    lazy(() => import('pages/Management/Resources/ResourceRequestsManage'))
);
const MgmtKillSwitchPage = Loadable(lazy(() => import('pages/Management/KillSwitch')));
const MgmtKillSwitchDetailPage = Loadable(lazy(() => import('pages/Management/KillSwitch/KillSwitchDetail')));

const UserProfilePage = Loadable(lazy(() => import('pages/Profile')));
const UserExpirioPage = Loadable(lazy(() => import('pages/Expirio')));

const ContainerListPage = Loadable(lazy(() => import('pages/Containers')));
const ContainerCreatePage = Loadable(lazy(() => import('pages/Containers/ContainerCreate')));
const ContainerUpdatePage = Loadable(lazy(() => import('pages/Containers/UpdateContainerMetadata')));

const JobScriptsListPage = Loadable(lazy(() => import('pages/JobScripts')));
const JobScriptCreatePage = Loadable(lazy(() => import('pages/JobScripts/JobscriptCreate')));
const JobScriptDuplicateScriptPage = Loadable(lazy(() => import('pages/JobScripts/JobscriptDuplicate')));

const MailResourceRequestPage = Loadable(lazy(() => import('pages/EmailRequest/ResourceRequest')));
const RemoteFileManagerPage = Loadable(lazy(() => import('pages/HPC')));

const MainRoutes = {
    path: '/',
    children: [
        {
            path: '/',
            element: (
                <AuthGuard>
                    <MainLayout />
                </AuthGuard>
            ),
            children: [
                {
                    path: mailResourceRequestPath(),
                    element: <MailResourceRequestPage />,
                },
                {
                    path: dashboardWorkflowsPath(),
                    element: <DashboardWorkflowsPage />,
                },
                {
                    path: dashboardResourceUsagePath(),
                    element: (
                        <InstanceGuard allowed={[InstanceType.LEXIS]}>
                            <DashboardResourceUsagePage />
                        </InstanceGuard>
                    ),
                },
                {
                    path: dashboardDatasetsPath(),
                    element: <MaintenanceComingSoonPage />,
                },
                {
                    path: dashboardGeneralPath(),
                    element: (
                        <InstanceGuard allowed={[InstanceType.LEXIS]}>
                            <DashboardGeneralPage />
                        </InstanceGuard>
                    ),
                },
                {
                    path: dashboardDdiPath(),
                    element: <DashboardDdiPage />,
                },
                {
                    path: workflowListPath(),
                    element: <WorkflowListPage />,
                },
                {
                    path: workflowDetailsPath(),
                    element: (
                        <WorkflowGuard>
                            <WorkflowDetailsPage />
                        </WorkflowGuard>
                    ),
                },
                {
                    path: workflowExecutePath(),
                    element: (
                        <WorkflowGuard>
                            <WorkflowExecutePage />
                        </WorkflowGuard>
                    ),
                },
                {
                    path: workflowExecutionDetailsPath(),
                    element: (
                        <WorkflowGuard>
                            <WorkflowExecutionDetailsPage />
                        </WorkflowGuard>
                    ),
                },
                {
                    path: workflowExecutionReExecutePath(),
                    element: (
                        <WorkflowGuard>
                            <WorkflowExecutionReExecutePage />
                        </WorkflowGuard>
                    ),
                },
                {
                    path: workflowExecutionLogsPath(),
                    element: (
                        <WorkflowGuard>
                            <WorkflowExecutionLogsPage />
                        </WorkflowGuard>
                    ),
                },
                {
                    path: datasetListPath(),
                    element: <DatasetListPage />,
                },
                {
                    path: datasetUploadPath(),
                    element: <DatasetUploadPage />,
                },
                {
                    path: datasetDetailsPath(),
                    element: <DatasetDetailsPage />,
                },
                {
                    path: datasetUpdatePath(),
                    element: <DatasetUpdatePage />,
                },
                {
                    path: datasetFileListPath(),
                    element: <DatasetFileListPage />,
                },
                {
                    path: datasetEditFilePath(),
                    element: <DatasetEditFilePage />,
                },
                {
                    path: datasetCreateEmptyPath(),
                    element: <DatasetCreateEmptyPage />,
                },
                {
                    path: dashboardTransferStatusStagePath(),
                    element: <DashboardTransferStatusStagePage />,
                },
                {
                    path: dashboardTransferStatusDeletePath(),
                    element: <DashboardTransferStatusDeletePage />,
                },
                {
                    path: projectListPath(),
                    element: <ProjectListPage />,
                },
                {
                    path: projectDetailsPath(),
                    element: <ProjectDetailsPage />,
                },
                {
                    path: projectOwnRequestsPath(),
                    element: <ProjectOwnRequestsPage />,
                },
                {
                    path: projectAccessRequestsPath(),
                    element: <ProjectAccessRequestsPage />,
                },
                {
                    path: projectResourceRequestsPath(),
                    element: <ProjectResourceRequestsPage />,
                },
                {
                    path: projectManageTagsPath(),
                    element: <ProjectManageTagsPage />,
                },
                {
                    path: projectCommandTemplatesMgmtPath(),
                    element: <ProjectCommandTemplatesMgmtPage />,
                },
                {
                    path: projectUsersPath(),
                    element: <ProjectUsersPage />,
                },
                {
                    path: userProfilePath(),
                    element: <UserProfilePage />,
                },
                {
                    path: userExpirioPath(),
                    element: <UserExpirioPage />,
                },
                ...(REMOTE_FILE_MANAGEMENT_ALLOWED
                    ? [
                          {
                              path: remoteFileManagerPath(),
                              element: <RemoteFileManagerPage />,
                          },
                      ]
                    : []),
                {
                    path: projectResourcesPath(),
                    element: <ProjectResourcesPage />,
                },
                {
                    path: projectResourceUsagePath(),
                    element: (
                        <InstanceGuard allowed={[InstanceType.LEXIS]}>
                            <ProjectResourceCyclopsPage />
                        </InstanceGuard>
                    ),
                },
                {
                    path: mgmtProvidersPath(),
                    element: <MgmtProvidersPage />,
                },
                {
                    path: mgmtLocationsPath(),
                    element: <MgmtLocationsPage />,
                },
                {
                    path: mgmtKillSwitchPath(),
                    element: <MgmtKillSwitchPage />,
                },
                {
                    path: mgmtCommandTemplatesPath(),
                    element: <ProjectCommandTemplatesMgmtPage />,
                },
                {
                    path: mgmtKillSwitchDetailPath(),
                    element: <MgmtKillSwitchDetailPage />,
                },
                {
                    path: mgmtUsersPath(),
                    element: <MgmtUsersPage />,
                },
                {
                    path: mgmtResourceAssignmentsPath(),
                    element: <MgmtResourceAssignmentsPage />,
                },
                {
                    path: mgmtResourcesPath(),
                    element: <MgmtResourcesPage />,
                },
                {
                    path: mgmtResourceRequestsPath(),
                    element: <MgmtResourceRequestsManagePage />,
                },
                {
                    path: mgmtProjectRequestsPath(),
                    element: <MgmtProjectRequestsPage />,
                },
                {
                    path: mgmtResourceRequestsPath(),
                    element: <MgmtResourceRequestsPage />,
                },
                {
                    path: containerListPath(),
                    element: <ContainerListPage />,
                },
                {
                    path: containerCreatePath(),
                    element: <ContainerCreatePage />,
                },
                {
                    path: containerUpdatePath(),
                    element: <ContainerUpdatePage />,
                },
                {
                    path: workflowCreateContainerConfigPath(),
                    element: <WorkflowCreateContainerConfigPage />,
                },
                {
                    path: workflowCreateContainerListPath(),
                    element: <WorkflowCreateContainerListPage />,
                },
                {
                    path: containerDetailsPath(),
                    element: <DatasetDetailsPage />,
                },
                {
                    path: workflowCreateHpcAppListPath(),
                    element: <WorkflowCreateHpcAppListPage />,
                },
                {
                    path: workflowCreateHpcAppConfigPath(),
                    element: <WorkflowCreateHpcAppConfigPage />,
                },
                {
                    path: workflowCreateJobScriptListPath(),
                    element: <WorkflowCreateJobScriptPage />,
                },
                {
                    path: workflowCreateJobScriptConfigPath(),
                    element: <WorkflowCreateJobScriptConfigPage />,
                },
                {
                    path: jobScriptsListPath(),
                    element: <JobScriptsListPage />,
                },
                {
                    path: jobScriptCreatePath(),
                    element: <JobScriptCreatePage />,
                },
                {
                    path: jobScriptDuplicatePath(),
                    element: <JobScriptDuplicateScriptPage />,
                },
                {
                    path: jobScriptExecutionLogsPath(),
                    element: <WorkflowExecutionLogsPage />,
                },
                {
                    path: workflowCreatePath(),
                    element: <WorkflowCreatePage />,
                },
                {
                    path: '/*',
                    element: <MaintenanceError404Page />,
                },
            ],
        },
        {
            path: error500Path(),
            element: <MaintenanceError500Page />,
        },
        {
            path: underConstructionPath(),
            element: <MaintenanceUnderConstructionPage />,
        },
        {
            path: 'coming-soon',
            element: <MaintenanceComingSoonPage />,
        },
    ],
};

export default MainRoutes;
